/**
 * Phase1104 — Order Status Semantic Banner
 *
 * Displays canonical state, notification eligibility, pickup governance,
 * and semantic warnings. UI-only, no DB writes, no Edge Function calls,
 * no LINE/runtime events.
 */
import { useMemo } from 'react';
import {
  getCanonicalOrderState,
  getCanonicalOrderEvent,
  getCustomerNotificationEligibility,
  getPickupGovernanceState,
  getOrderStatusDisplay,
  type LegacyOrderStatus,
} from '@/modules/order/order-status-semantics';

interface Props {
  status: LegacyOrderStatus;
  previousStatus?: LegacyOrderStatus | null;
}

export default function OrderSemanticBanner({ status, previousStatus }: Props) {
  const display = useMemo(() => getOrderStatusDisplay(status), [status]);

  const canonicalState = useMemo(
    () => getCanonicalOrderState({ legacy_status: status }),
    [status],
  );

  const canonicalEvent = useMemo(
    () =>
      getCanonicalOrderEvent({
        legacy_status: status,
        previous_legacy_status: previousStatus ?? null,
        event_source: 'status_transition',
      }),
    [status, previousStatus],
  );

  const notificationEligibility = useMemo(
    () =>
      getCustomerNotificationEligibility({
        legacy_status: status,
        canonical_state: canonicalState,
        canonical_event: canonicalEvent,
        previous_legacy_status: previousStatus ?? null,
      }),
    [status, canonicalState, canonicalEvent, previousStatus],
  );

  const pickupGovernanceState = useMemo(
    () => getPickupGovernanceState({ legacy_status: status, canonical_state: canonicalState }),
    [status, canonicalState],
  );

  // ─── Approved-specific warning ───
  const showApprovedWarning = status === 'approved';
  // ─── Renting-specific warning ───
  const showRentingWarning = status === 'renting';

  const pickupGovLabels: Record<string, { label: string; color: string }> = {
    pre_contract: { label: '契約前', color: 'text-slate-500' },
    pre_handoff: { label: '待交件', color: 'text-sky-600' },
    ready_not_picked_up: { label: '已備妥待取', color: 'text-teal-600' },
    post_handoff_active: { label: '已交件 / 租借中', color: 'text-amber-700' },
    return_or_completion_pending: { label: '歸還確認中', color: 'text-gray-600' },
    terminal_pre_handoff: { label: '終止（交件前）', color: 'text-gray-500' },
    terminal_normal: { label: '正常結案', color: 'text-gray-500' },
    legal_review: { label: '法務審查', color: 'text-red-600' },
    unknown: { label: '不明', color: 'text-gray-400' },
  };

  const govInfo = pickupGovLabels[pickupGovernanceState] ?? pickupGovLabels.unknown;

  return (
    <div className="space-y-3">
      {/* ─── Semantic State Row ─── */}
      <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 whitespace-nowrap">舊狀態</span>
          <span className="text-xs font-mono font-semibold text-gray-600 bg-white border border-gray-200 px-1.5 py-0.5 rounded">
            {status}
          </span>
        </div>
        <i className="ri-arrow-right-s-line text-gray-400 text-xs"></i>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 whitespace-nowrap">語義狀態</span>
          <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
            {display.adminLabel}
          </span>
        </div>
        <i className="ri-arrow-right-s-line text-gray-400 text-xs"></i>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 whitespace-nowrap">交件治理</span>
          <span className={`text-xs font-medium ${govInfo.color}`}>
            {govInfo.label}
          </span>
        </div>
      </div>

      {/* ─── Notification Eligibility Row ─── */}
      <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-xs text-gray-400 whitespace-nowrap">通知事件</span>
        {canonicalEvent ? (
          <span className="text-xs font-mono text-gray-700 bg-white border border-gray-200 px-1.5 py-0.5 rounded">
            {canonicalEvent}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">無（非轉換節點）</span>
        )}
        <span className="text-xs text-gray-400">·</span>
        <div className="flex items-center gap-1">
          {notificationEligibility.eligible ? (
            <span className="inline-flex items-center gap-1 text-xs text-teal-700 font-medium">
              <i className="ri-checkbox-circle-line text-teal-500"></i>
              可發送
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-forbid-line text-gray-400"></i>
              不發送
              {notificationEligibility.deny_reason && (
                <span className="text-gray-400 font-mono">({notificationEligibility.deny_reason})</span>
              )}
            </span>
          )}
        </div>
        {notificationEligibility.template_key && (
          <>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-400 font-mono">{notificationEligibility.template_key}</span>
          </>
        )}
      </div>

      {/* ─── Approved: NOT ready_for_pickup warning ─── */}
      {showApprovedWarning && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-sm font-semibold text-amber-800">訂單已接受（非器材備妥）</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              訂單已接受，但不代表器材已備妥可取件。可取件需另行明確操作/事件。
              <br />
              <span className="font-mono text-amber-600">approved = accepted ≠ ready_for_pickup</span>
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-200">
                <i className="ri-forbid-line text-xs"></i>
                已阻擋 order.ready_for_pickup 自動推定
              </span>
              <span className="text-xs text-amber-500 italic">Mock / Pending backend</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Renting: contract active / no ordinary cancel warning ─── */}
      {showRentingWarning && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
          <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-sm font-semibold text-orange-800">契約生效中 — 風險已移轉</p>
            <p className="text-xs text-orange-700 mt-0.5 leading-relaxed">
              器材已交付，租借契約生效，責任及風險已移轉至客戶。
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 border border-orange-200">
                <i className="ri-lock-line text-xs"></i>
                不可普通取消
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 border border-orange-200">
                <i className="ri-admin-line text-xs"></i>
                回退需店長以上核准
              </span>
              <span className="text-xs text-orange-500 italic">
                如需取消請走歸還 / 爭議 / 協議終止流程
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}